import { Offcanvas, Form, Row, Col, Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";

interface EventFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

/**
 * Tag interface for tag objects returned by the API.
 * Add fields here as needed if the API returns additional properties.
 */
interface Tag {
  _id: string;
  name: string;
}

type FormData = {
  amount: number;
  discountCode: string;
  applyOn: string;
  applyId?: string;
  mode: string;
  startDate: string;
  endDate: string;
  maxUse?: number;
};

const AddEventDiscount = ({ show, handleClose, data }: EventFormData) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>();

  const [isLoading, setLoading] = useState(false);
  const applyOn = watch("applyOn");
  const startDate = watch("startDate");
  const [applyOptions, setApplyOptions] = useState<any[]>([]);
  useEffect(() => {
    if (show) {
      console.log("Editing Data:", data);
      reset({
        amount: data?.amount || "",
        discountCode: data?.discountCode || "",
        applyOn: data?.applyOn || "",
        mode: data?.mode || "",
        startDate: data?.startDate ? data.startDate.split("T")[0] : undefined,
        endDate: data?.endDate ? data.endDate.split("T")[0] : undefined,
        applyId: data?.applyId?._id || "",
        maxUse: data?.maxUse ?? "",
      });
    } else {
      reset({});
    }
  }, [show, data, reset]);
  useEffect(() => {
    if (!applyOn) return;
    fetchData();
  }, [applyOn, data]);

  useEffect(() => {
  if (
    data?._id &&
    data?.applyId?._id &&
    applyOptions.length > 0
  ) {
    setValue("applyId", data.applyId._id);
  }
}, [applyOptions, data, setValue]);
  // ✅ Fetch paginated tags
  const fetchData = async () => {
    let endpoint = "";

    if (applyOn === "EVENT") endpoint = "events/getEventsDropDown";
    if (applyOn === "PLAN") endpoint = "plan/get-plan";
    if (applyOn === "REPORT")
      endpoint = "resources/get-resources?page=1&limit=1000&resource_type=INDUSTRY";

    try {
      const res: any = await WebService.getAPI({ action: endpoint });
      let options = [];
      if (applyOn === "PLAN") {
        options = res.data || [];
      } else if (applyOn === "EVENT") {
        options = res.data || [];
      } else {
        options = res.data?.data || [];
      }

      // ✅ IMPORTANT FIX (EDIT MODE)
      if (
        data?._id && // edit mode
        applyOn === "EVENT" &&
        data?.applyId // selected event exists
      ) {
        const selectedEvent = data.applyId;

        const alreadyExists = options.some(
          (item: any) =>
            (item._id || item.value) === selectedEvent._id
        );

        if (!alreadyExists) {
          options.unshift({
            _id: selectedEvent._id,
            title: selectedEvent.title,
          });
        }
      }

      setApplyOptions(options);

      // if (data?._id && data?.applyId?._id) {
      //   setValue("applyId", data.applyId._id);
      // }
    } catch (err) {
      console.error(err);
      setApplyOptions([]);
    }
  };
  const onSubmit = (request: FormData) => {
    setLoading(true);
    const apiCall = data?._id
      ? WebService.putAPI({
        action: `discount/updateContent/${data._id}`,
        body: request,
      })
      : WebService.postAPI({
        action: `discount/addContent`,
        body: request,
      });

    apiCall
      .then((res: any) => {
        toast.success(res.message);
        reset();
        handleClose();
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error.response?.data?.message || "An error occurred");
      });
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => {
        reset();
        handleClose();
      }}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {data ? "Edit Coupon" : "Add Coupon"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form>
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Main Content
            </span>
            <Form.Group className="mb-2" controlId="applyOn">
              <Form.Label className="label">
                Discount On <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                {...register("applyOn", { required: "Type is required" })}
                defaultValue=""
              >
                <option value="" disabled hidden>
                  Select Type
                </option>
                <option value="EVENT">Event</option>
                <option value="PLAN">Membership</option>
                <option value="REPORT">Industry Report</option>
              </Form.Select>
              {errors.applyOn && (
                <div className="error-message">{errors.applyOn.message}</div>
              )}
            </Form.Group>
            {applyOn && (
              <Form.Group className="mb-2" controlId="applyId">
                <Form.Label className="label">
                  Select {applyOn} <span className="text-danger">*</span>
                </Form.Label>

                <Form.Select
                  {...register("applyId", {
                    required: "Please select an option",
                  })}
                  defaultValue={data?.applyId || ""}
                >
                  <option value="">Select {applyOn}</option>

                  {applyOptions.map((item: any) => (
                    <option value={item.value || item._id} key={item.value || item._id}>
                      {item.label || item.title}
                    </option>
                  ))}
                </Form.Select>

                {errors.applyId && (
                  <div className="error-message">{errors.applyId.message}</div>
                )}
              </Form.Group>
            )}
            {/* discountCode */}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Coupon Code <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter coupon code"
                {...register("discountCode", {
                  required: "Coupon Code is required",
                })}
              />
              {errors.discountCode && (
                <div className="error-message">
                  {errors.discountCode.message}
                </div>
              )}
            </Form.Group>

            {/* type */}
            <Form.Group className="mb-2" controlId="mode">
              <Form.Label className="label">
                Type <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                {...register("mode", { required: "Type is required" })}
                defaultValue=""
              >
                <option value="" disabled hidden>
                  Select Type
                </option>
                <option value="FLAT">Flat</option>
                <option value="PERCENTAGE">Percentage</option>
              </Form.Select>
              {errors.mode && (
                <div className="error-message">{errors.mode.message}</div>
              )}
            </Form.Group>
            {/* Amount */}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                {watch("mode") === "PERCENTAGE" ? "Percentage" : "Amount"} <span className="text-danger">*</span>
              </Form.Label>

              <div className="position-relative">
                <Form.Control
                  type="number"
                  placeholder={watch("mode") === "PERCENTAGE" ? "Enter percentage" : "Enter amount"}
                  {...register("amount", { required: watch("mode") === "PERCENTAGE" ? "Percentage is required" : "Amount is required" })}
                  style={{ paddingRight: "40px" }} // space for suffix
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                    ];
                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />

                {/* Suffix inside input */}
                <span
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#6c757d",
                    fontSize: "14px",
                  }}
                >
                  {watch("mode") === "PERCENTAGE" ? "%" : "₹"}
                </span>
              </div>

              {errors.amount && (
                <div className="error-message">{errors.amount.message}</div>
              )}
            </Form.Group>

            {/* Maximum Use */}
            <Form.Group className="mb-2" controlId="maxUse">
              <Form.Label className="label">Maximum Use <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter maximum usage limit (optional)"
                {...register("maxUse", {
                  required: "Maximum Use is required",
                  min: {
                    value: 1,
                    message: "Minimum value should be 1",
                  },
                })}
                min={1}
                onKeyDown={(e) => {
                  const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"];
                  if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
              {errors.maxUse && (
                <div className="error-message">{errors.maxUse.message}</div>
              )}
            </Form.Group>

            {data?._id && (
              <Form.Group className="mb-2" controlId="usedCount">
                <Form.Label className="label">
                  Used Count
                </Form.Label>

                <Form.Control
                  type="number"
                  value={data?.usedCount || 0}
                  readOnly
                  style={{
                    backgroundColor: "#f8f9fa",
                    cursor: "not-allowed",
                  }}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-2" controlId="eventDate">
              <Form.Label className="label">
                Start Date <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                {...register("startDate", {
                  required: "Start Date is required",
                })}
              />
              {errors.startDate && (
                <div className="error-message">{errors.startDate.message}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="eventDate">
              <Form.Label className="label">
                End Date <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                min={startDate || undefined}
                {...register("endDate", { required: "End Date is required" })}
              />
              {errors.endDate && (
                <div className="error-message">{errors.endDate.message}</div>
              )}
            </Form.Group>
          </Card>
        </Form>
      </Offcanvas.Body>
      <div className="d-flex justify-content-end p-3">
        <CustomButton
          isLoading={isLoading}
          label="Save"
          className="w-100"
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Offcanvas>
  );
};

export default AddEventDiscount;
